import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { GroupBase, MenuListProps } from 'react-select';
import { components } from 'react-select';
import type { OptionTypeBase } from '../../components/Select';
import addTestId from '../../utils/addTestId';
import type { CategorizedSelectCustomProps } from './CategorizedEntitySelect';
import * as styles from './EntitySelectMenuList.module.scss';

const isAnimatingMenuListContext = createContext(false);
export const useIsAnimatingMenuList = () =>
  useContext(isAnimatingMenuListContext);

type MenuType = 'preview' | 'list';

interface MenuListContentProps {
  children: React.ReactNode;
  type: MenuType;
  setHeight: (height: number, type: MenuType) => void;
}

const MenuListContent = ({
  children,
  type,
  setHeight,
}: MenuListContentProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isAnimating = useIsAnimatingMenuList();

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        const height = contentRef.current?.getBoundingClientRect().height;
        if (height) {
          setHeight(height, type);
        }
      });
      resizeObserver.observe(contentRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [setHeight, type]);

  return (
    <div
      className={classNames(styles.content, {
        [styles.isAnimating]: isAnimating,
      })}
      ref={contentRef}
    >
      {children}
    </div>
  );
};

const DEFAULT_STYLES = {
  position: 'absolute',
  y: 0,
  x: 0,
  opacity: 1,
  width: '100%',
} as const;

type EntitySelectMenuListProps<
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = boolean,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = MenuListProps<OptionType, IsMulti, GroupType> & {
  selectProps: MenuListProps<OptionType, IsMulti, GroupType>['selectProps'] &
    Partial<CategorizedSelectCustomProps>;
};

const EntitySelectMenuList = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = boolean,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
  props: EntitySelectMenuListProps<OptionType, IsMulti, GroupType>,
) => {
  const menuListRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const {
    selectProps: { selectedEntityCategory },
  } = props;

  // Enable animation after the first render
  // because the react-select is deciding the menulist height and position
  useLayoutEffect(() => {
    setShouldAnimate(true);
  }, []);

  const menuType: MenuType = selectedEntityCategory ? 'preview' : 'list';

  const menuListMaxHeight = Number(
    props.getStyles('menuList', props).maxHeight,
  );

  const setHeight = useCallback(
    (height: number, type: MenuType) => {
      if (shouldAnimate && type === menuType) {
        menuListRef.current?.setAttribute(
          'style',
          `height: ${height}px; max-height: ${menuListMaxHeight}px;`,
        );
      }
    },
    [shouldAnimate, menuType, menuListMaxHeight],
  );

  useEffect(() => {
    if (menuListRef.current) {
      menuListRef.current.scrollTop = 0;
    }
  }, [selectedEntityCategory]);

  const hiddenStyles = {
    ...DEFAULT_STYLES,
    x: selectedEntityCategory ? '100%' : '-100%',
    opacity: 0,
  };

  const content = (
    <MenuListContent setHeight={setHeight} type={menuType}>
      {props.children}
    </MenuListContent>
  );

  return (
    <components.MenuList
      {...props}
      className={styles.menuList}
      innerRef={(ref) => {
        // @ts-ignore
        props.innerRef?.(ref);
        menuListRef.current = ref;
      }}
    >
      <isAnimatingMenuListContext.Provider value={isAnimating}>
        {shouldAnimate ? (
          <AnimatePresence initial={false}>
            <motion.div
              ref={contentRef}
              key={menuType}
              initial={hiddenStyles}
              animate={DEFAULT_STYLES}
              exit={hiddenStyles}
              transition={{ type: 'tween', duration: 0.25 }}
              {...addTestId('entitySelectMenuList')}
              id="entitySelectMenuList"
              onAnimationStart={() => {
                setIsAnimating(true);
              }}
              onAnimationComplete={() => {
                setIsAnimating(false);
              }}
            >
              {content}
            </motion.div>
          </AnimatePresence>
        ) : (
          content
        )}
      </isAnimatingMenuListContext.Provider>
    </components.MenuList>
  );
};

export default EntitySelectMenuList;
